import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";

const empty = {
  sku: "", slug: "", name_en: "", name_nl: "", description_en: "", description_nl: "",
  price: 0, compare_at_price: null, currency: "EUR", category: "home-living",
  images: [], stock: 0, featured: false, best_seller: false, new_arrival: false, tags: [],
  status: "active", vat_rate: null,
};

const AdminProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const isNew = id === "new" || !id;
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showMedia, setShowMedia] = useState(false);

  useEffect(() => {
    apiClient.get("/categories").then((r) => setCategories(r.data));
    apiClient.get("/admin/media").then((r) => setMedia(r.data));
    if (!isNew) {
      apiClient.get("/admin/products").then((r) => {
        const p = r.data.find((x) => x.product_id === id);
        if (p) setForm({ ...empty, ...p });
      });
    }
  }, [id, isNew]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      setMedia((m) => [data, ...m]);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        sku: form.sku, slug: form.slug, name_en: form.name_en, name_nl: form.name_nl,
        description_en: form.description_en, description_nl: form.description_nl,
        price: parseFloat(form.price), compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
        currency: form.currency, category: form.category, images: form.images,
        stock: parseInt(form.stock), featured: !!form.featured, best_seller: !!form.best_seller,
        new_arrival: !!form.new_arrival, tags: form.tags || [], status: form.status || "active",
        vat_rate: form.vat_rate === "" || form.vat_rate == null ? null : parseFloat(form.vat_rate),
      };
      if (isNew) {
        await apiClient.post("/products", body);
        toast.success(lang === "nl" ? "Aangemaakt" : "Created");
      } else {
        await apiClient.put(`/products/${id}`, body);
        toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
      }
      navigate("/admin/products");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-product-edit-page">
      <Link to="/admin/products" className="font-label text-ink-muted hover:text-ink flex items-center gap-1.5 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> {lang === "nl" ? "Producten" : "Products"}</Link>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{isNew ? (lang === "nl" ? "Nieuw product" : "New product") : form.name_en || "Edit"}</h1>
        <div className="flex gap-2">
          <Link to="/admin/products" className="btn-secondary text-sm">Cancel</Link>
          <button disabled={busy} data-testid="admin-product-save" className="btn-primary text-sm">{busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <div className="border border-black/10 bg-white p-6 space-y-3">
            <div className="font-label text-ink-muted">{lang === "nl" ? "Basisgegevens" : "Basic info"}</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="SKU" required value={form.sku} onChange={(v) => upd("sku", v)} testid="product-sku" />
              <Field label="Slug" required value={form.slug} onChange={(v) => upd("slug", v)} testid="product-slug" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name (EN)" required value={form.name_en} onChange={(v) => upd("name_en", v)} testid="product-name-en" />
              <Field label="Naam (NL)" required value={form.name_nl} onChange={(v) => upd("name_nl", v)} testid="product-name-nl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Textarea label="Description (EN)" value={form.description_en} onChange={(v) => upd("description_en", v)} testid="product-desc-en" />
              <Textarea label="Beschrijving (NL)" value={form.description_nl} onChange={(v) => upd("description_nl", v)} testid="product-desc-nl" />
            </div>
          </div>

          <div className="border border-black/10 bg-white p-6 space-y-3">
            <div className="font-label text-ink-muted">{lang === "nl" ? "Afbeeldingen" : "Images"}</div>
            <div className="flex flex-wrap gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-24 h-28 bg-secondary-warm group" data-testid={`product-image-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => upd("images", form.images.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 bg-white border border-black flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="w-24 h-28 border border-dashed border-black/30 flex flex-col items-center justify-center cursor-pointer hover:bg-secondary-warm" data-testid="product-image-upload">
                <Upload className="w-4 h-4 text-ink-muted" />
                <span className="text-[10px] font-label mt-1 text-ink-muted">Upload</span>
                <input type="file" accept="image/*" hidden onChange={uploadImage} />
              </label>
              <button type="button" onClick={() => setShowMedia(true)} className="w-24 h-28 border border-dashed border-black/30 flex flex-col items-center justify-center hover:bg-secondary-warm text-[10px] font-label text-ink-muted">{lang === "nl" ? "Mediatheek" : "Media library"}</button>
            </div>
            <div className="text-xs text-ink-muted">{lang === "nl" ? "Of plak een URL:" : "Or paste a URL:"}</div>
            <div className="flex gap-2">
              <input type="url" placeholder="https://…" id="product-image-url" className="border border-black/15 px-3 py-2 text-sm flex-1" data-testid="product-image-url" />
              <button type="button" onClick={() => {
                const el = document.getElementById("product-image-url");
                if (el?.value) { upd("images", [...form.images, el.value]); el.value = ""; }
              }} className="btn-secondary text-xs">Add</button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-black/10 bg-white p-6 space-y-3">
            <div className="font-label text-ink-muted">{lang === "nl" ? "Prijs & Voorraad" : "Pricing & Stock"}</div>
            <Field label={lang === "nl" ? "Prijs (€)" : "Price (€)"} type="number" step="0.01" required value={form.price} onChange={(v) => upd("price", v)} testid="product-price" />
            <Field label={lang === "nl" ? "Vergelijkingsprijs (€)" : "Compare-at (€)"} type="number" step="0.01" value={form.compare_at_price || ""} onChange={(v) => upd("compare_at_price", v)} testid="product-compare-price" />
            <Field label={lang === "nl" ? "Voorraad" : "Stock"} type="number" required value={form.stock} onChange={(v) => upd("stock", v)} testid="product-stock" />
            <label className="block">
              <div className="text-xs font-label text-ink-muted mb-1">{lang === "nl" ? "BTW-tarief" : "VAT rate"}</div>
              <select data-testid="product-vat-rate" value={form.vat_rate ?? ""} onChange={(e) => upd("vat_rate", e.target.value === "" ? null : parseFloat(e.target.value))} className="border border-black/15 px-3 py-2 text-sm w-full bg-white">
                <option value="">{lang === "nl" ? "Standaard (uit instellingen)" : "Default (from settings)"}</option>
                <option value={21}>21% — {lang === "nl" ? "Algemeen" : "Standard"}</option>
                <option value={9}>9% — {lang === "nl" ? "Verlaagd (voedsel/boeken)" : "Reduced (food/books)"}</option>
                <option value={0}>0% — {lang === "nl" ? "Vrijgesteld" : "Zero"}</option>
              </select>
            </label>
          </div>
          <div className="border border-black/10 bg-white p-6 space-y-3">
            <div className="font-label text-ink-muted">{lang === "nl" ? "Categorie & Vlaggen" : "Category & Flags"}</div>
            <label className="block text-xs font-label text-ink-muted">Status</label>
            <select value={form.status || "active"} onChange={(e) => upd("status", e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" data-testid="product-status">
              <option value="active">Active — {lang === "nl" ? "zichtbaar in winkel" : "visible in store"}</option>
              <option value="draft">Draft — {lang === "nl" ? "verborgen, work in progress" : "hidden, work in progress"}</option>
              <option value="hidden">Hidden — {lang === "nl" ? "verborgen voor klanten" : "hidden from customers"}</option>
            </select>
            <label className="block text-xs font-label text-ink-muted mt-3">Category</label>
            <select value={form.category} onChange={(e) => upd("category", e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" data-testid="product-category">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name_en}</option>)}
            </select>
            <CheckRow label="Featured" checked={form.featured} onChange={(v) => upd("featured", v)} testid="product-featured" />
            <CheckRow label="Best seller" checked={form.best_seller} onChange={(v) => upd("best_seller", v)} testid="product-bestseller" />
            <CheckRow label="New arrival" checked={form.new_arrival} onChange={(v) => upd("new_arrival", v)} testid="product-new" />
          </div>
        </div>
      </div>

      {showMedia && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowMedia(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">{lang === "nl" ? "Mediatheek" : "Media library"}</div>
              <button type="button" onClick={() => setShowMedia(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
              {media.map((m) => (
                <button key={m.media_id} type="button" onClick={() => { upd("images", [...form.images, m.url]); setShowMedia(false); }} className="aspect-square bg-secondary-warm hover:ring-2 hover:ring-ink overflow-hidden">
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

const Field = ({ label, value, onChange, testid, ...rest }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" {...rest} />
  </label>
);
const Textarea = ({ label, value, onChange, testid }) => (
  <label className="block col-span-1">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);
const CheckRow = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm">
    <input type="checkbox" data-testid={testid} checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

export default AdminProductEdit;
